'use client';

import { useEffect, useState } from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import ProcessTimeline from './ProcessTimeline';
import FAQSection from './FAQSection';
import ConsultationCTA from './ConsultationCTA';

interface Service {
  number: string;
  title: string;
  description: string;
  features: string[];
  timeline: string;
  investment: string;
}

interface TimelineStep {
  phase: string;
  title: string;
  description: string;
  duration: string;
}

interface FAQ {
  question: string;
  answer: string;
}

const ServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: 'Digital Growth Services',
    subtitle: 'What We Offer',
    description:
      'Helping Triangle area businesses and online brands grow through strategic digital services tailored to your unique needs',
  };

  const services: Service[] = [
    {
      number: '01',
      title: 'Social Media Management',
      description:
        'Build a consistent, engaging brand presence across social platforms. We handle content creation, strategic scheduling, and eye-catching visuals designed to grow your audience and drive engagement.',
      features: [
        'Content creation and curation',
        'Strategic posting schedules',
        'Engaging visual design',
        'Community engagement',
        'Growth-focused strategy',
      ],
      timeline: '4-8 Weeks',
      investment: 'Custom',
    },
    {
      number: '02',
      title: 'Digital Paid Marketing',
      description:
        'Data-driven paid advertising campaigns backed by nearly six years of experience since 2020. We focus on ROI-driven targeting and continuous optimization to maximize your marketing investment.',
      features: [
        'Campaign strategy and setup',
        'Audience targeting and research',
        'Ad creative development',
        'Performance optimization',
        'Detailed ROI reporting',
      ],
      timeline: '2-4 Weeks',
      investment: 'Custom',
    },
    {
      number: '03',
      title: 'Business Automation',
      description:
        'Streamline your operations and reduce time spent on repetitive tasks. We implement smart automation solutions that improve efficiency and free you to focus on growing your business.',
      features: [
        'Workflow automation setup',
        'Task management systems',
        'Integration with existing tools',
        'Process optimization',
        'Training and support',
      ],
      timeline: '3-6 Weeks',
      investment: 'Custom',
    },
    {
      number: '04',
      title: 'Website Building',
      description:
        'Custom websites for local restaurants, bars, service businesses, and online brands. We create clean, modern, mobile-friendly designs that convert visitors into customers.',
      features: [
        'Custom responsive design',
        'Mobile-first approach',
        'SEO optimization',
        'Fast loading speeds',
        'Easy content management',
      ],
      timeline: '6-10 Weeks',
      investment: 'Custom',
    },
  ];

  const processSteps: TimelineStep[] = [
    {
      phase: '01',
      title: 'Discovery & Strategy',
      description:
        'We start by understanding your business, goals, and target audience. This foundation ensures every strategy is tailored to your specific needs.',
      duration: '1 Week',
    },
    {
      phase: '02',
      title: 'Planning & Setup',
      description:
        'Detailed planning and setup of campaigns, content calendars, or development roadmaps. We ensure everything is aligned before execution.',
      duration: '1-2 Weeks',
    },
    {
      phase: '03',
      title: 'Execution & Launch',
      description:
        'Implementation of strategies with careful attention to detail. We launch campaigns, publish content, or deploy websites with precision.',
      duration: '2-6 Weeks',
    },
    {
      phase: '04',
      title: 'Optimization & Growth',
      description:
        'Continuous monitoring and optimization based on performance data. We refine strategies to maximize results and drive sustained growth.',
      duration: 'Ongoing',
    },
  ];

  const faqs: FAQ[] = [
    {
      question: 'Do you work with businesses outside the Triangle area?',
      answer:
        'Yes! While we\'re based in Raleigh and specialize in Triangle area businesses, we also work with online brands nationwide. Our digital services work seamlessly regardless of location.',
    },
    {
      question: 'What makes Valinaire different from other agencies?',
      answer:
        'We combine financial analysis expertise with creative marketing execution—a rare blend that helps us understand both ROI metrics and creative strategies. Plus, we offer personalized service rather than the one-size-fits-all approach of large agencies.',
    },
    {
      question: 'How do you determine pricing for services?',
      answer:
        'Every business has unique needs, so we create custom proposals based on your specific goals, scope, and budget. Contact us for a free consultation and personalized quote tailored to your business.',
    },
    {
      question: 'What is your typical project timeline?',
      answer:
        'Our project timelines vary by service, but most clients are onboarded within 1–2 weeks.',
    },
    {
      question: 'Do you offer ongoing support after project completion?',
      answer:
        'Absolutely. Many of our clients work with us on an ongoing basis for social media management, paid marketing optimization, and website maintenance. We\'re here to support your continued growth.',
    },
    {
      question: 'What information do you need to get started?',
      answer:
        'Just reach out via our contact form, email, or text. We\'ll schedule a free consultation to discuss your business goals, current challenges, and how we can help. From there, we\'ll create a customized proposal.',
    },
  ];

  const ctaData = {
    title: "Let\'s Grow Your Business Together",
    description:
      'Schedule a free consultation to discuss your goals and explore how Valinaire can help your business achieve measurable digital growth.',
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse">
          <div className="h-[60vh] bg-muted" />
          <div className="px-6 lg:px-12 py-24">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-96 bg-muted" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ServiceHero
        title={heroData.title}
        subtitle={heroData.subtitle}
        description={heroData.description}
      />

      <section className="px-6 lg:px-12 py-24 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 lg:mb-24">
            <p className="text-xs font-cta font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Service Offerings
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold tracking-tight text-primary mb-6">
              Tailored Solutions
            </h2>
            <p className="text-lg lg:text-xl font-body text-text-secondary max-w-3xl mx-auto">
              Each service is designed to deliver exceptional value through
              strategic thinking and refined execution
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </div>
      </section>

      <ProcessTimeline steps={processSteps} />

      <FAQSection faqs={faqs} />

      <ConsultationCTA title={ctaData.title} description={ctaData.description} />
    </div>
  );
};

export default ServicesInteractive;